import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { ApiError } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Button, TextField } from "../compose/controls";
import { ArrowRight } from "../compose/Icon";
import { ErrorState, Spinner } from "../compose/states";
import { useAsync } from "../lib/useAsync";

/**
 * Set-password `/set-password?token=…` — the invite-acceptance flow. Validates
 * the token (GET /invites/:token), shows the invited email + workspace, then
 * takes a name + password to create the account and drop the new hire straight
 * into their workspace (POST /invites/:token/accept mints a session).
 */
export function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const invite = useAsync(() => api.getInvite(token), [token]);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return setError("Enter your name.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setBusy(true);
    try {
      await api.acceptInvite(token, { name: name.trim(), password });
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not create your account.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="auth__inner">
        <div className="auth__brand">
          <h1 className="auth__wordmark">elaborate</h1>
          <div className="auth__tagline">Set up your account</div>
        </div>

        <div className="auth__card">
          {invite.loading ? (
            <Spinner label="Checking your invite…" />
          ) : invite.error || !invite.data ? (
            <ErrorState
              title="This invite link isn't valid"
              body="It may have expired or already been used. Ask your workspace admin for a fresh link."
              action={
                <Button variant="secondary" onClick={() => navigate("/login")}>
                  Go to sign in
                </Button>
              }
            />
          ) : (
            <form onSubmit={submit} noValidate data-testid="set-password-form">
              <p className="dialog__sub" style={{ marginBottom: 18 }}>
                You&rsquo;re joining <strong>{invite.data.workspace_name}</strong> as{" "}
                <strong>{invite.data.email}</strong>.
              </p>
              {error ? (
                <div className="auth__error" role="alert" data-testid="sp-error">
                  {error}
                </div>
              ) : null}
              <TextField
                label="Your name"
                value={name}
                data-testid="sp-name"
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
              <TextField
                label="Password"
                type="password"
                autoComplete="new-password"
                value={password}
                data-testid="sp-password"
                hint="At least 8 characters."
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextField
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                data-testid="sp-confirm"
                onChange={(e) => setConfirm(e.target.value)}
              />
              <Button
                type="submit"
                variant="primary"
                block
                disabled={busy}
                data-testid="sp-submit"
                icon={<ArrowRight size={16} strokeWidth={1.5} />}
              >
                {busy ? "Creating account…" : "Create account & continue"}
              </Button>
            </form>
          )}
        </div>
        <div className="auth__foot">Logged-in only. No public access.</div>
      </div>
    </div>
  );
}
